import { useState, useMemo } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useHelpSettings, useHelpCategories, useHelpArticles, useHelpArticle, useRecentHelpArticles } from "@/hooks/useHelpCenter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { CategoryArticles } from "@/pages/help/CategoryArticles";
import { SearchResults } from "@/pages/help/SearchResults";
import { HelpSearchDropdown } from "@/components/help/HelpSearchDropdown";
import { useArticleViewTracking } from "@/hooks/useArticleViewTracking";
import {
  Rocket,
  User,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Shield,
  HelpCircle,
  Search,
  ChevronRight,
  ArrowLeft,
  Clock,
} from "lucide-react";
import type { HelpArticleContent, ContentBlock } from "@/types/helpArticle";
import { getVideoEmbedUrl } from "@/lib/videoEmbed";

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  User,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Shield,
  HelpCircle,
};

function HelpCenterDisabled() {
  return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <HelpCircle className="h-16 w-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Help Center Unavailable</h1>
        <p className="text-muted-foreground max-w-md">
          The Help Center is currently unavailable. Please check back later or contact support if you need immediate assistance.
        </p>
      </div>
    </DashboardLayout>
  );
}

function HelpCategoryList() {
  const navigate = useNavigate();
  const { data: categories, isLoading: categoriesLoading } = useHelpCategories();
  const { data: articles, isLoading: articlesLoading } = useHelpArticles();
  const { data: recentArticles, isLoading: recentLoading } = useRecentHelpArticles(5);

  const activeCategories = categories?.filter(c => c.is_active);

  if (categoriesLoading || articlesLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Dropdown */}
      <HelpSearchDropdown />

      {/* Category Grid - responsive stacking */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
        {activeCategories?.map(category => {
          const Icon = iconMap[category.icon || 'HelpCircle'] || HelpCircle;
          const categoryArticles = articles?.filter(
            a => a.category_id === category.id && a.is_published
          );
          
          return (
            <Card
              key={category.id}
              className="cursor-pointer transition-all duration-200 hover:bg-muted/50 hover:border-primary/30 hover:shadow-md active:scale-[0.98]"
              onClick={() => navigate(`/help/category/${category.id}`)}
            >
              <CardHeader className="pb-2 sm:pb-3 p-4 sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base sm:text-lg leading-tight">{category.name}</CardTitle>
                    {category.description && (
                      <CardDescription className="line-clamp-2 mt-1 text-sm">
                        {category.description}
                      </CardDescription>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                </div>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4 sm:px-6 sm:pb-6">
                <p className="text-sm text-muted-foreground">
                  {categoryArticles?.length || 0} article{categoryArticles?.length !== 1 ? 's' : ''}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Articles Section */}
      {recentArticles && recentArticles.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Recently Added
          </h3>
          <div className="space-y-2">
            {recentArticles.map(article => (
              <Card
                key={article.id}
                className="cursor-pointer hover:bg-muted/50 hover:border-primary/30 transition-all duration-200 active:scale-[0.99]"
                onClick={() => navigate(`/help/article/${article.id}`)}
              >
                <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate text-sm sm:text-base">{article.title}</h4>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(article.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-xs hidden sm:inline-flex">
                    {article.category?.name}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 sm:hidden" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Loading skeleton for recent articles */}
      {recentLoading && (
        <div className="mt-8">
          <Skeleton className="h-6 w-40 mb-4" />
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Render a content block for the article
function renderContentBlock(block: ContentBlock) {
  switch (block.type) {
    case "paragraph":
      return (
        <div 
          key={block.id} 
          className="text-muted-foreground leading-relaxed mb-4 [&_p]:mb-3 last:[&_p]:mb-0"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content) }}
        />
      );

    case "heading":
      return (
        <h2
          key={block.id}
          className="text-xl font-semibold text-foreground mt-8 mb-4 pb-2 border-b border-border first:mt-0"
        >
          {block.content}
        </h2>
      );

    case "callout":
      return (
        <div
          key={block.id}
          className="border-l-4 border-primary bg-primary/5 p-4 my-6 rounded-r"
        >
          {block.title && (
            <p className="font-semibold text-primary mb-1">{block.title}</p>
          )}
          <div 
            className="text-sm text-foreground [&_p]:mb-2 last:[&_p]:mb-0"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content) }}
          />
        </div>
      );

    case "image":
      return (
        <figure key={block.id} className="my-6">
          <img
            src={block.url}
            alt={block.alt}
            className="w-full max-w-full h-auto rounded-lg object-contain"
          />
          {block.caption && (
            <figcaption className="text-center text-sm text-muted-foreground mt-2 px-2">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case "video": {
      const embedUrl = getVideoEmbedUrl(block.url);
      return (
        <figure key={block.id} className="my-6">
          {block.title && (
            <p className="text-sm font-medium mb-2">{block.title}</p>
          )}
          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={block.title || "Embedded video"}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                Video unavailable
              </div>
            )}
          </div>
        </figure>
      );
    }

    default:
      return null;
  }
}

function HelpArticleView({ articleId }: { articleId: string }) {
  const navigate = useNavigate();
  const { data: article, isLoading, error } = useHelpArticle(articleId);
  
  // Track article view
  useArticleViewTracking(articleId);

  // Parse body as JSON blocks or fallback to legacy HTML
  const parsedContent = useMemo(() => {
    if (!article?.body) return null;
    
    try {
      const content = JSON.parse(article.body) as HelpArticleContent;
      if (content?.blocks && Array.isArray(content.blocks)) {
        return content;
      }
    } catch {
      // Not JSON, treat as legacy HTML
    }
    return null;
  }, [article?.body]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !article || !article.is_published) {
    return (
      <div className="text-center py-12">
        <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Article Not Found</h2>
        <p className="text-muted-foreground mb-4">This article may have been removed or is not available.</p>
        <Button onClick={() => navigate("/help")}>Back to Help Center</Button>
      </div>
    );
  }

  const Icon = iconMap[article.category?.icon || 'HelpCircle'] || HelpCircle;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation breadcrumb */}
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(`/help/category/${article.category_id}`)}
          className="h-10 w-10 shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm">
          <Icon className="h-3 w-3" />
          <span className="truncate max-w-[150px] sm:max-w-none">{article.category?.name}</span>
        </Badge>
      </div>

      {/* Article container with consistent styling */}
      <div className="bg-background rounded-lg border">
        {/* Article header - responsive padding */}
        <div className="px-4 py-6 sm:px-6 sm:py-8 border-b bg-muted/30">
          <p className="text-xs sm:text-sm text-muted-foreground mb-2">
            Help Center • {article.category?.name}
          </p>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2 sm:mb-3 leading-tight">{article.title}</h1>
          {article.summary && (
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{article.summary}</p>
          )}
        </div>

        {/* Article body - responsive padding */}
        <div className="px-4 py-6 sm:px-6 sm:py-8">
          {parsedContent ? (
            // Render new block-based content
            <article className="help-article-content space-y-4">
              {parsedContent.blocks.map((block) => renderContentBlock(block))}
            </article>
          ) : (
            // Render legacy HTML content
            <div 
              className="help-article-content prose prose-slate max-w-none
                prose-headings:text-foreground prose-headings:font-semibold
                prose-h2:text-lg prose-h2:sm:text-xl prose-h2:mt-6 prose-h2:sm:mt-8 prose-h2:mb-3 prose-h2:sm:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-border
                prose-h3:text-base prose-h3:sm:text-lg prose-h3:mt-4 prose-h3:sm:mt-6 prose-h3:mb-2 prose-h3:sm:mb-3
                prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-3 prose-p:sm:mb-4 prose-p:text-sm prose-p:sm:text-base
                prose-ul:my-3 prose-ul:sm:my-4 prose-ul:space-y-2
                prose-ol:my-3 prose-ol:sm:my-4 prose-ol:space-y-2
                prose-li:text-muted-foreground prose-li:leading-relaxed prose-li:text-sm prose-li:sm:text-base
                prose-strong:text-foreground prose-strong:font-semibold
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-img:max-w-full prose-img:h-auto prose-img:rounded-lg
                [&_.help-article-intro]:text-sm [&_.help-article-intro]:sm:text-base [&_.help-article-intro]:text-muted-foreground [&_.help-article-intro]:leading-relaxed [&_.help-article-intro]:mb-4 [&_.help-article-intro]:sm:mb-6
                [&_.help-callout]:my-4 [&_.help-callout]:sm:my-6 [&_.help-callout]:p-3 [&_.help-callout]:sm:p-4 [&_.help-callout]:bg-info/5 [&_.help-callout]:border-l-4 [&_.help-callout]:border-l-info [&_.help-callout]:rounded-r
                [&_.help-callout_p]:text-foreground [&_.help-callout_p]:mb-0 [&_.help-callout_p]:text-sm [&_.help-callout_p]:sm:text-base
                [&_.help-footer-note]:mt-6 [&_.help-footer-note]:sm:mt-8 [&_.help-footer-note]:pt-4 [&_.help-footer-note]:sm:pt-6 [&_.help-footer-note]:border-t [&_.help-footer-note]:border-border
                [&_.help-footer-note_p]:text-xs [&_.help-footer-note_p]:sm:text-sm [&_.help-footer-note_p]:text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.body) }} 
            />
          )}
        </div>
      </div>

      {/* Bottom navigation - full width on mobile */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-2 sm:pt-4">
        <Button 
          variant="outline" 
          onClick={() => navigate(`/help/category/${article.category_id}`)}
          className="w-full sm:w-auto h-11 sm:h-10"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          <span className="truncate">Back to {article.category?.name}</span>
        </Button>
      </div>
    </div>
  );
}

// Simple HTML sanitizer to prevent XSS
/**
 * Sanitizes admin-authored article HTML before it reaches
 * dangerouslySetInnerHTML.
 *
 * The previous implementation was regex-based and stripped only QUOTED event
 * handlers (on\w+="..." / on\w+='...'), so all of these survived it:
 *   <img src=x onerror=alert(1)>      (unquoted handler)
 *   <svg onload=alert(1)>
 *   <a href="javascript:alert(1)">
 *   <style>, <object>, <embed>
 * Regex-based HTML sanitization cannot be made correct, so this delegates to
 * DOMPurify with its standard HTML profile.
 *
 * Input is admin-authored, so this is an admin-to-user escalation rather than
 * anonymous XSS — but the RLS visibility gate on help_articles is correct and
 * unchanged; the client was the weak point.
 */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html ?? "", { USE_PROFILES: { html: true } });
}

export default function Help() {
  const { categoryId, articleId } = useParams<{ categoryId?: string; articleId?: string }>();
  const location = useLocation();
  const { data: settings, isLoading: settingsLoading } = useHelpSettings();
  
  const isSearchPage = location.pathname === "/help/search";

  if (settingsLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!settings?.help_enabled) {
    return <HelpCenterDisabled />;
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {!categoryId && !articleId && !isSearchPage && (
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-2">Help Center</h1>
            <p className="text-muted-foreground">
              Find answers to common questions and learn how to get the most out of EAB Connect.
            </p>
          </div>
        )}

        {articleId ? (
          <HelpArticleView articleId={articleId} />
        ) : categoryId ? (
          <CategoryArticles categoryId={categoryId} />
        ) : isSearchPage ? (
          <SearchResults />
        ) : (
          <HelpCategoryList />
        )}
      </div>
    </DashboardLayout>
  );
}
